// English. Same keys, nesting and array lengths as ka.ts (typed via Dict).
import type { Dict } from "./ka";

const en: Dict = {
  meta: {
    title: "HOTGAME — Bring the heat!",
    description: "Georgian party games on your phone: Bluffmania, Spy, Match Party, True or False and the Joker Notebook.",
  },
  nav: {
    games: "Games",
    join: "Join with a code",
    joker: "Joker Notebook",
    login: "Sign in",
    account: "Profile",
    brand: "Brand book",
    admin: "Admin",
    home: "Home",
    menu: "Menu",
  },
  common: {
    play: "Play",
    back: "Back",
    next: "Next",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    loading: "Loading…",
    retry: "Try again",
    copy: "Copy",
    copied: "Copied!",
    share: "Share",
    players: "players",
    playersN: "Players: {n}",
    minutes: "{n} min",
    you: "You",
    host: "Host",
    bot: "Bot",
    audience: "Audience",
    points: "points",
    pts: "{n} pts",
    sound: "Sound",
    mute: "Mute",
    unmute: "Unmute",
    on: "On",
    off: "Off",
    or: "or",
    seconds: "{n} sec",
    round: "Round {n}",
    of: "{a} / {b}",
    // Language names stay in their own language, so everyone can find theirs.
    lang: { ka: "ქართული", en: "English", ru: "Русский" },
    langShort: { ka: "ქარ", en: "ENG", ru: "РУС" },
  },
  home: {
    heroTitle: "Bring the heat!",
    // Width of heroTitle in em at weight 900 (measured), so the headline can fill the row exactly.
    heroEm: 7.8,
    heroSub: "Six games to play with your friends. Phone in hand — and off we go!",
    create: "Create a room",
    joinPlaceholder: "Code",
    joinGo: "Join",
    gamesTitle: "Pick a game",
    gamesSub: "Every game works in Georgian, English and Russian — and the questions come in your language too.",
    howTitle: "How to play",
    how1: "One of you creates a room — on a phone, a laptop or the TV.",
    how2: "Everyone else jumps in with the QR code or a 4-letter code. No app needed.",
    how3: "You play, you laugh, you argue. We keep score.",
    tryTitle: "Try it now",
    tryQ: "In 1932, the Australian army went to war against ____ — and lost.",
    tryA: "emus",
    tryLies: ["kangaroos", "rabbits", "koalas"],
    tryNote: "That was the “Great Emu War”: soldiers armed with machine guns couldn’t beat 20,000 emus.",
    trySub: "One answer is true. The rest are lies. Which one is it?",
    tryRight: "You got it! That really happened 🔥",
    tryWrong: "Fooled you! The truth was:",
    tryAgain: "One more",
    adultTitle: "18+ mode",
    adultSub: "For after the kids are asleep. Spicy questions about love lives, parties and the stuff nobody says out loud. Only the host can switch it on.",
    statQuestions: "questions",
    statGames: "games",
    statLangs: "languages",
    footerTag: "Hot games for cold nights.",
    rights: "© 2026 HOTGAME",
  },
  games: {
    bluff: {
      name: "Bluffmania",
      tagline: "Bluff. Detect. Win.",
      short: "Make up a convincing lie, fool your friends — and sniff out the truth yourself.",
      players: "2–12",
      time: "15–25",
      rules: [
        "A weird-but-true fact pops up on screen with one word missing.",
        "Everyone writes a lie — one so good the others think it’s the truth.",
        "Then you vote for the real answer: the truth earns you +1000, and every friend you fool +500.",
        "Points double in round two and triple in the Hot Final.",
      ],
    },
    spy: {
      name: "Spy",
      tagline: "One of you is lying.",
      short: "Everyone knows the secret word — except the spy. 3–5 questions later: who’s the spy?",
      players: "3–16",
      time: "5–10",
      rules: [
        "Everyone gets the secret word. The spy (or spies) only gets the topic.",
        "The app asks 3–5 questions and you take turns answering — don’t give too much away, but don’t sound like the spy either.",
        "After the last question, everyone votes for who they think the spy is.",
        "A caught spy gets one last shot: guess the word and still win.",
      ],
    },
    match: {
      name: "Match Party",
      tagline: "Khinkali seeking khinkali.",
      short: "A memory game with cards straight from a Georgian feast. Find the pairs, build combos and dodge the bombs.",
      players: "1–6",
      time: "5–10",
      rules: [
        "Flip two cards per turn. If they match, the pair is yours — and you go again.",
        "Pairs in a row build a combo: +100, +150, +200…",
        "A bomb costs you points and the turn passes on.",
        "Take turns on one phone, or everyone plays on their own.",
      ],
    },
    truf: {
      name: "True or False",
      tagline: "10 seconds. True or false?",
      short: "A lightning quiz: a statement hits the screen and you call it — true or false. The faster you are, the more you score.",
      players: "1–30",
      time: "5",
      rules: [
        "A statement appears — some will blow your mind, some are just famous myths.",
        "Call it: true or false. Fast right answers score more.",
        "Three right in a row starts a streak — and the bonus keeps growing 🔥",
      ],
    },
    guess: {
      name: "Guess",
      tagline: "See it. Say it. Pass the turn.",
      short: "A quick-fire picture duel played by voice: shout the answer and the game checks it for you. Everyone gets 60 seconds for the whole match.",
      players: "2–6",
      time: "3–6",
      rules: [
        "Everyone gets 60 seconds. A right answer passes the turn. A mistake or a skip costs you 3 seconds.",
        "Say your answer out loud — in English or Georgian. No buttons to press.",
        "Say “skip” to get a new picture — but it costs you 3 seconds.",
        "Whoever runs out of time first loses.",
      ],
    },
    joker: {
      name: "Joker Notebook",
      tagline: "Let the notebook do the math.",
      short: "Scorekeeping for Georgian Joker: tap who bids what and who takes how many — the notebook does the rest.",
      players: "3–4",
      time: "∞",
    },
  },
  cats: {
    weird: "WTF facts",
    animals: "Animals",
    body: "Human body",
    science: "Science",
    space: "Space",
    history: "History",
    laws: "Weird laws",
    money: "Business & money",
    places: "Places",
    geo: "Geography",
    food: "Food & drink",
    popculture: "Pop culture",
    sport: "Sports",
    georgia: "Georgia",
    myths: "Myths",
    tech: "Tech",
    night: "18+ after dark",
  },
  create: {
    title: "New room",
    online: "Everyone on their own phone",
    onlineSub: "A room code and a QR — friends join from their own phones.",
    local: "One shared phone",
    localSub: "Pass the phone around — no internet needed.",
    solo: "Solo vs bots",
    soloSub: "Warm up against bots until your friends show up.",
    yourName: "Your name",
    namePh: "e.g. Nino",
    avatar: "Your character",
    roomLang: "Question language",
    go: "Create room",
    nameRequired: "Type your name first",
    howToPlay: "How to play",
  },
  join: {
    title: "Join a room",
    code: "Room code",
    codePh: "ABCD",
    go: "Join",
    asAudience: "Join as audience",
    notFound: "We couldn’t find that room. Check the code.",
    full: "The room’s full — but you can join as audience.",
    started: "The game’s already on — you’ll jump in next round.",
    gameIs: "Game: {game}",
  },
  lobby: {
    title: "Lobby",
    code: "Room code",
    scan: "Scan to join",
    invite: "Invite friends",
    copyLink: "Copy link",
    whatsapp: "WhatsApp",
    telegram: "Telegram",
    messenger: "Messenger",
    shareText: "Come play on HOTGAME! We’re playing {game}. Code: {code}",
    waitingHost: "Waiting for the host to start…",
    start: "Start",
    needPlayers: "You need at least {n} players",
    addBot: "+ Bot",
    removeBot: "Remove bot",
    kick: "Kick",
    makeHost: "Make host",
    settings: "Settings",
    settingsHostOnly: "Only the host can change settings",
    rename: "Change name",
    becomeAudience: "Switch to audience",
    becomePlayer: "Switch to player",
    leave: "Leave room",
    leaveConfirm: "Sure you want to leave the room?",
    lang: "Question language: {lang}",
    adult: "18+ questions",
    adultConfirmTitle: "Turn on 18+ mode",
    adultConfirmText: "18+ questions include jokes about sex, alcohol and dating. Only turn it on if every player is 18 or older.",
    adultConfirmYes: "We’re all 18+ — turn it on",
    players: "Players ({n})",
    audience: "Audience ({n})",
    offline: "Lost connection",
    tv: "TV mode",
  },
  room: {
    connecting: "Connecting to the room…",
    reconnecting: "Connection lost — trying again…",
    gone: "This room is gone or has expired.",
    kicked: "The host kicked you out of the room.",
    toHome: "Back to home",
    paused: "Paused",
    pausedSub: "The host paused the game",
    pause: "Pause",
    resume: "Resume",
    skip: "Skip",
    playAgain: "Play again!",
    backToLobby: "Back to lobby",
    newRound: "Next round",
    audienceNote: "You’re in the audience — sit back and laugh 🍿",
    hostControls: "Host controls",
    scoreboard: "Scoreboard",
    winner: "Winner!",
    winners: "Winners!",
    report: "Something wrong with this question?",
    reportThanks: "Thanks! We’ll take a look.",
    reportReasons: { wrong: "The fact is wrong", typo: "Grammar / typo", boring: "It’s boring", offensive: "It’s offensive", other: "Something else" },
  },
  bluff: {
    act0: "Round 1",
    act1: "Round 2",
    act2: "Hot Final",
    actSub0: "Time to warm up",
    actSub1: "Double points ×2",
    actSub2: "One question. Points ×3",
    question: "Question {n}/{total}",
    writeTitle: "Make up a lie",
    writePh: "Your lie…",
    send: "Send",
    change: "Change",
    help: "Lie for me",
    helpLeft: "{n} left",
    helpUse: "Use it",
    yourLie: "Your lie",
    waitingOthers: "Waiting for the others…",
    autoLie: "Time ran out — we wrote a lie for you (half points).",
    voteTitle: "Which one’s the truth?",
    voteHint: "You can’t pick your own lie. Tap 😂 if an answer cracks you up.",
    yours: "Your lie",
    reread: "Read it again",
    voted: "Vote’s in!",
    picked: "Your pick",
    likes: "Like",
    revealLie: "{name}’s lie!",
    revealLies: "A lie by {names}!",
    revealHouse: "Our lie!",
    revealAuto: "{name}’s lie (auto)",
    revealTruth: "The truth!",
    fooled: "Fell for it:",
    found: "Found it:",
    nobodyFound: "Nobody found the truth 😱",
    everyoneFound: "Everyone found the truth!",
    onlyYou: "Only you found it! 🔥",
    gotYou: "{name} got you!",
    gotYouHouse: "Our lie got you 😏",
    youFooled: "Your lie fooled {n}!",
    scoresTitle: "Scoreboard",
    finalTitle: "Final results",
    awards: {
      liar: "King of Lies",
      detective: "Detective",
      gullible: "Most Gullible",
      liked: "Crowd Favorite",
      bestLie: "Best Lie",
    },
    awardsSub: {
      liar: "Fooled others ×{n}",
      detective: "Found the truth ×{n}",
      gullible: "Fell for it ×{n}",
      liked: "😂 ×{n}",
      bestLie: "“{text}” — fooled {n}",
    },
    nextIn: "Next question…",
    skipWrite: "Skip to the vote",
    skipVote: "Skip to the results",
    errors: {
      is_truth: "That’s the truth! 😅 Think up a different lie.",
      too_close: "Too close to the truth — try something else.",
      taken: "Someone already wrote exactly that. Try another one!",
      own_option: "You can’t pick your own lie 😉",
      no_help_left: "You’re out of help.",
    },
  },
  spy: {
    rolesTitle: "Secret roles",
    tapToSee: "Tap to see your role",
    hideFromOthers: "Cover your screen — no peeking!",
    youAreSpy: "You’re the spy!",
    spyHint: "You don’t know the word. Listen to the others and try not to blow your cover.",
    spyTheme: "Topic: {pack}",
    spyNoTheme: "You don’t even know the topic 😈",
    secretWord: "Secret word",
    civHint: "The spy doesn’t know this word. Don’t give it away — but don’t look suspicious either!",
    spiesInGame: "Spies in this game: {n}",
    seen: "Got it",
    seenCount: "Roles seen: {a}/{b}",
    startQuestions: "Start the questions",
    qTitle: "Question {n}/{total}",
    order: "Answering in this order:",
    yourTurn: "Your turn! Answer out loud",
    turnOf: "Now answering: {name}",
    doneTurn: "Done ✓",
    nextQuestion: "Next question",
    answerPh: "Your answer (keep it short)…",
    answered: "Answered: {a}/{b}",
    answersTitle: "Answers",
    toVote: "Go to the vote",
    voteTitle: "Who’s the spy?",
    voteSub: "Pick one suspect.",
    votedFor: "You voted for {name}",
    votesIn: "Votes in: {a}/{b}",
    closeVote: "Close the vote",
    groupAccuse: "Who did the group pick?",
    accuse: "Accuse",
    revealTitle: "The spy was…",
    revealTitlePlural: "The spies were…",
    caught: "Caught! 🎯",
    escaped: "Got away! 🏃",
    wrongAccused: "{name} wasn’t a spy 😬",
    noOneAccused: "Split vote — nobody got caught!",
    guessTitle: "Last chance!",
    guessSub: "Guess the secret word — get it right and you still win.",
    guessWait: "The caught spy is racking their brain for the word…",
    guessed: "{name} guessed the word! 😱",
    guessedWrong: "{name} guessed wrong: “{word}”",
    civWin: "Civilians win!",
    spyWin: "Spies win!",
    wordWas: "The word was: {word}",
    roundPts: "+{n}",
    passTo: "Pass the phone to {name}",
    passReady: "I’m {name} — show me",
    passHide: "Got it — hide it",
    localSetup: "Players",
    addPlayer: "+ Player",
    playerPh: "Player {n}",
    packs: "Topics",
    allPacks: "All topics",
    spies: "Number of spies",
    spiesAuto: "Auto",
    questions: "Questions",
    mode: "Answers",
    modeVoice: "Out loud",
    modeText: "Typed",
    spyHintSetting: "The spy knows the topic",
    qTime: "Time per question",
    qTimeNone: "No limit",
  },
  match: {
    turn: "{name}’s turn",
    yourTurn: "Your turn!",
    match: "Match! +{n}",
    combo: "Combo ×{n}! +{p}",
    miss: "No match",
    boom: "Boom! 💥 −{n}",
    timeout: "Time’s up!",
    pairsLeft: "Pairs left: {n}",
    pairs: "Pairs",
    theme: "Cards",
    bombs: "Bombs",
    bombsNone: "None",
    turnTime: "Time per turn",
    turnTimeNone: "No limit",
    size: "Board size",
    themes: { supra: "Georgian feast", animals: "Animals", party: "Party", tbilisi: "Emoji mix", emoji: "Faces" },
    hotseat: "One phone",
    addLocal: "+ Player",
  },
  truf: {
    statement: "Statement {n}/{total}",
    true: "True",
    false: "False",
    itsTrue: "It’s true! ✓",
    itsFalse: "It’s false! ✗",
    correct: "Correct! +{n}",
    wrong: "Wrong",
    noAnswer: "Too slow!",
    streak: "{n} in a row 🔥",
    answered: "Answered: {a}/{b}",
    count: "Number of statements",
    time: "Time to answer",
    cats: "Topics",
    allCats: "All topics",
    final: "Final scoreboard",
    accuracy: "{a}/{b} right",
  },
  bluffSettings: {
    length: "Game length",
    short: "Short (5)",
    normal: "Regular (8)",
    long: "Long (11)",
    speed: "Timer",
    chill: "Chill",
    normalSpeed: "Normal",
    fast: "Fast",
    cats: "Categories",
    allCats: "All categories",
    houseLie: "Throw in our lie too",
    help: "“Lie for me” — how many times",
    jokes: "Host jokes",
  },
  joker: {
    title: "Joker Notebook",
    sub: "Tap who bids what and who takes how many — the notebook counts the points, khishti and premiums for you. Nothing gets lost when you refresh.",
    newGame: "New game",
    continue: "Continue",
    saved: "Saved games",
    noSaved: "No games yet. Start a new one!",
    deleteGame: "Delete",
    deleteConfirm: "Delete this game for good?",
    players: "Players",
    playerPh: "Player {n}",
    type: "Game type",
    typeStandard: "Standard (1–8, 9×4, 8–1, 9×4)",
    typeNines: "Nines (9×16)",
    khishti: "Khishti",
    khishtiNone: "We don’t play it",
    khishtiHint: "A penalty when you bid 1 or more and take nothing",
    premium: "Premium at the end of a pulka",
    premiumOn: "Best score doubles",
    premiumErase: "Everyone else’s best score gets erased",
    firstDealer: "First dealer",
    start: "Open the notebook",
    pulka: "Pulka {n}",
    deal: "Deal {n}",
    cards: "{n}-card deal",
    dealer: "Dealer: {name}",
    stepBids: "Who bids what?",
    stepTaken: "Who took how many?",
    bidOf: "{name} bids",
    takenOf: "{name} took",
    pass: "Pass",
    forbidden: "{name} can’t bid {n} — the total would hit {cards}",
    sumBids: "Total bid: {a} / {b}",
    sumTaken: "Total taken: {a} / {b}",
    takenMismatch: "Tricks taken must add up to exactly {n}",
    saveDeal: "Save",
    editDeal: "Edit",
    editTitle: "Fix the deal",
    deleteDeal: "Delete the last deal",
    undo: "Undo",
    total: "Total",
    premiumBadge: "Premium",
    khishtiBadge: "Khishti",
    exact: "Exact",
    finished: "Game over!",
    rename: "Rename",
    reset: "Start over",
    resetConfirm: "Every deal will be wiped. Are you sure?",
    rules: "Rules & scoring",
    rulesList: [
      "Take exactly what you bid: 50 × your bid + 50 (a pass scores 50).",
      "Bid every card and take them all: 100 × the number of cards.",
      "Miss your bid: 10 × the tricks you took.",
      "Khishti: you bid 1 or more and took nothing.",
      "The dealer (who bids last) can’t bid a number that makes the bids add up to the number of cards.",
      "Premium: hit your bid in every deal of a pulka, and your best score of that pulka doubles.",
    ],
    sync: "Synced with your account",
    localOnly: "Saved on this device. Sign in to have it on your other devices too.",
    shareImage: "Save as image",
    leader: "In the lead: {name}",
  },
  auth: {
    loginTitle: "Sign in",
    registerTitle: "Sign up",
    withPassword: "Password",
    withSms: "SMS code",
    withEmail: "Email code",
    identifier: "Username, email or phone",
    username: "Username",
    usernameHint: "Letters a–z, numbers, _ or . (3–20)",
    email: "Email",
    phone: "Phone number",
    country: "Country",
    password: "Password",
    passwordHint: "At least 8 characters",
    displayName: "In-game name (optional)",
    terms: "I agree to the terms and confirm I’m over 13",
    login: "Sign in",
    register: "Sign up",
    noAccount: "No account yet?",
    haveAccount: "Already have an account?",
    forgot: "Forgot your password?",
    sendCode: "Send code",
    codeSent: "Code sent to {to}",
    code: "6-digit code",
    verify: "Verify",
    resend: "Resend",
    resendIn: "Resend in {n}s",
    devCode: "Test mode: no SMS/email provider is connected yet. Your code is {code}",
    newPassword: "New password",
    resetTitle: "Reset your password",
    resetDone: "Password changed — you’re signed in.",
    logout: "Sign out",
    guestNote: "You can play without an account. An account keeps your stats and Joker notebooks.",
    profile: "Profile",
    verifyPhone: "Verify phone",
    verifyEmail: "Verify email",
    verified: "Verified",
    notVerified: "Not verified",
    adultToggle: "Show 18+ content",
    adultConfirm: "I confirm I’m 18 or older",
    stats: "Stats",
    played: "Played",
    won: "Won",
    best: "Best",
    changePassword: "Change password",
    currentPassword: "Current password",
    saved: "Saved!",
    errors: {
      username_invalid: "That username won’t work",
      username_taken: "That username is taken",
      email_invalid: "That email doesn’t look right",
      email_taken: "That email is already registered",
      phone_invalid: "That number doesn’t look right",
      phone_taken: "That number is already registered",
      password_short: "Your password needs at least 8 characters",
      password_long: "That password is too long",
      password_digits: "Your password can’t be only numbers",
      terms_required: "Please accept the terms",
      bad_credentials: "Wrong username or password",
      otp_invalid: "That code is wrong",
      otp_expired: "That code has expired — ask for a new one",
      otp_locked: "Too many tries — ask for a new code",
      delivery_failed: "We couldn’t send the code. Try again later.",
      rate_limited: "Too many tries, too fast. Give it a moment.",
      auth_required: "Sign in first",
      age_confirm_required: "Please confirm your age",
      conflict: "That account already exists",
      generic: "Something went wrong. Try again.",
    },
  },
  err: {
    not_found: "Not found",
    not_host: "Only the host can do that",
    not_player: "You’re not playing this round",
    bad_phase: "You can’t do that right now",
    bad_input: "Something’s off with what you entered",
    too_few_players: "Not enough players",
    room_full: "The room is full",
    name_taken: "That name’s taken",
    kicked: "You got kicked from the room",
    rate_limited: "Whoa, slow down 🙂",
    is_truth: "That’s the truth!",
    too_close: "Too close to the truth",
    taken: "Someone already wrote that",
    own_option: "You can’t pick your own",
    already: "You already did that",
    not_your_turn: "It’s not your turn",
    no_help_left: "You’re out of help",
    self_vote: "You can’t accuse yourself 😄",
    no_content: "No questions match these settings",
    paused: "The game is paused",
    avatar_taken: "Someone already has this avatar — pick another",
    server: "Server error",
    name_required: "Enter a name",
    network: "Connection problem",
    csrf: "Security error — refresh the page",
  },
  /** Bluffmania host lines (read by the narrator; 2–3 variants each, picked at random). */
  host: {
    welcome: [
      "Welcome to Bluffmania! Where the truth is weird — and lies are your weapon!",
      "Alright, my masters of deception — let’s get this started!",
      "Welcome, welcome! Tonight we find out who tells the most beautiful lies.",
    ],
    act0: ["Round one! Time to warm up.", "Here comes round one. Get those lies ready!"],
    act1: ["Round two! Now the points are doubled.", "Heads up: in round two, every lie is worth double!"],
    act2: ["It’s the Hot Final! One question, triple points. It all comes down to this!", "The final! Even the leader could crash and burn right here."],
    write: ["Cook up a lie!", "Now it’s time to lie."],
    hurry: ["Ten seconds left!", "Hurry up, time’s running out!"],
    timeup: ["Time’s up!", "Pens down!"],
    vote: ["Now find the truth!", "One of these answers is true. But which one?", "Pick the truth — and don’t get fooled!"],
    lie: ["That was a lie!", "It’s a lie!", "Oh ho, that one was made up!"],
    fooledMany: ["Look how many of you fell for that!", "Bravo, you magnificent liar!", "Somebody give that lie an Oscar!"],
    truth: ["And the truth is…", "And now… the truth!", "And the real answer is…"],
    nobody: ["Nobody found the truth!", "Oh no — every single one of you got fooled!"],
    everyone: ["Everyone found the truth! What a clever bunch you are.", "Everybody got it — you’ll have to lie better next time!"],
    scores: ["Let’s check the scoreboard!", "So… who’s in the lead?"],
    winner: ["And our winner is…", "Who’s our new king of lies? There they are!"],
    outro: ["Thanks for playing! See you again soon.", "What a game! How about one more?"],
    /** "Question two!" … (index = question number − 1) */
    qnum: ["Question one!", "Question two!", "Question three!", "Question four!", "Question five!", "Question six!", "Question seven!", "Question eight!", "Question nine!", "Question ten!"],
    /** said instead of the plain "write" line on 2–3 questions per game (can be switched off) */
    joke: [
      "Come on — write a lie so good even your mother-in-law would buy it!",
      "If you've ever said “I'm on my way” while still in bed — you're ready for this!",
      "“The bus never came” — a timeless classic. Now let's hear yours!",
      "“I'm five minutes away!” — that's the confidence we need.",
      "Write it as convincingly as “My diet starts tomorrow.”",
      "“Just one more drink” — the oldest lie at any party. Your turn!",
      "A good lie is like khachapuri: hot, round and very convincing!",
      "“My phone was on silent” — old trick. Invent a new one!",
      "Remember “The dog ate my homework”? That talent is back!",
      "Write a lie so good you'll believe it yourself tomorrow!",
      "Imagine you're writing a weather forecast: bold, confident and totally made up!",
      "Lie like a CV: “Fluent in five languages.”",
      "Lie as beautifully as a dating-app photo!",
      "“I'll just pop in for a minute” — and stayed till morning. That's the spirit!",
      "“Just a little, I'm not hungry” — and then a third plate. Now that's talent!",
      "Write a lie so good the toastmaster raises a glass to it!",
      "Lie like a kid: “It wasn't me!”",
      "Make it harder to read than a doctor's handwriting!",
      "Lie like a shop assistant: “That colour really suits you!”",
      "“I got it on sale” — the champion of household lies. Your turn!",
      "Write it down before your conscience catches up!",
      "Write a lie so good Wikipedia would publish it!",
      "Switch on the talent that got you through your last exam!",
      "Lie like a friend who says: “No, I'm not mad at all.”",
      "“I'll definitely call you tomorrow” — make it that warm!",
      "Lie like a mechanic: “Just one small part needs replacing.”",
      "“I'm starting to study right now” — the student anthem. Go!",
      "“I promise, that was the last time” — write something just as sincere!",
      "Beat my lie. You know the one: “This question is easy!”",
      "Lie as confidently as a driver who “knows the way” and won't switch on the GPS.",
      "“One more episode, then bed” — and it's three in the morning. Your turn!",
      "“No, really, I couldn't eat another bite,” says the guest, reaching for dumpling number four. Go!",
      "“My battery died” — the oldest lie in the book. Find a new one!",
      "It's lie time! We won't tell your mum.",
      "Write boldly! Some people get Oscars for lying — you get points.",
      "Write a lie worth passing down like grandma's secret recipe!",
      "Make it so good even my artificial intelligence can't tell!",
      "The perfect lie needs a pinch of truth — like a good cocktail needs a squeeze of lemon!",
      "“I already ate,” you tell your mum. Now use that same talent here!",
      "“It's not my fault, the Wi-Fi went down” — simple and convincing. Like that!",
      "Lie like a taxi driver: “This is the shortest way!”",
      "Lie as innocently as a kid with chocolate all over their face.",
      "A lie should be like a great dance move: smooth, quick and impressive!",
      "“Tomorrow I'm getting up early” — said every single night. Give us something new!",
      "“I have read and agree to the terms and conditions” — the biggest lie on Earth. Beat it!",
      "“I've already paid my share” — a dinner-table classic. Your turn!",
      "Write a lie so good the truth gets jealous!",
      "“Almost ready!” says everyone who isn't even dressed yet. Like that!",
      "Lie as calmly as a cat that just knocked over a vase.",
      "Write a lie even my grandma couldn't see through!",
    ],
    /** the first question of a replay */
    welcomeBack: ["Wow, you're back? So you liked it! Thanks for coming back!", "Another game? I love it! Let's lie even better this time.", "Thanks for staying! I knew one game wouldn't be enough."],
    prevWinner: ["Last game's champion — your crown is in danger!", "A warning to last game's winner: someone's coming for that crown!"],
    prevLoser: ["Somebody had a rough last game — let's give them a big cheer! Today is their day!", "To whoever came last: don't worry, today is your revenge!"],
    /** said right after the player's name (Georgian: the name in the dative, "მარიამს, …") */
    prevWinnerName: ["Last game's champion — your crown is in danger!", "A warning to last game's winner: someone's coming for that crown!"],
    prevLoserName: ["Somebody had a rough last game — let's give them a big cheer! Today is their day!", "To whoever came last: don't worry, today is your revenge!"],
  },
  shouts: {
    fooledAll: ["Everyone fell for it! 🔥", "Dude, what a lie!", "Give this an Oscar!"],
    truthFound: ["Sherlock!", "Nothing gets past you!", "Sniffed it out!"],
    nobody: ["Nobody found the truth 😱", "Hard to believe it’s real, huh?"],
    hot: ["Hot hot hot! 🔥", "It’s heating up!", "Whoa, whoa!"],
    spyCaught: ["Busted!", "Those shades won’t save you now!"],
    spyFree: ["The spy got away!", "James Bond was among us 😎"],
  },
  brand: {
    title: "Brand book",
  },
  avatars: {
    khinkali: "Khinkali",
    churchkhela: "Churchkhela",
    pomegranate: "Pomegranate",
    grapes: "Saperavi",
    khachapuri: "Khachapuri",
    pepper: "Pepper",
    kantsi: "Kantsi",
    dog: "Nagazi",
    cat: "Cat",
    bear: "Bear",
    fox: "Fox",
    owl: "Owl",
    sheep: "Sheep",
    tur: "Tur",
    tamada: "Tamada",
    spy: "Agent",
    car: "Car",
    rooster: "Rooster",
    cow: "Cow",
  },
  prefs: {
    title: "Settings",
    sound: "Sound effects",
    narrator: "Narrator voice",
    narratorHint: "The narrator reads the questions out loud. Turn it on for just one device per room — the host’s phone or the TV.",
    haptics: "Vibration",
    volume: "Volume",
    language: "Site language",
  },
  local: {
    playersTitle: "Who’s playing?",
    addPlayer: "+ Player",
    addBot: "+ Bot",
    playerPh: "Player {n}",
    min: "You need at least {n} players",
    start: "Let’s go!",
    passTo: "Pass the phone",
    tapWhenReady: "Tap when you’re ready",
    hideAndPass: "Hide it and pass it on",
    remove: "Remove",
    resumeTitle: "Pick up where you left off?",
    resumeText: "There’s an unfinished game on this device.",
    resume: "Continue",
    discard: "Start fresh",
  },
  demo: {
    note: "This is a preview: the games run right in your browser, with bots or on one phone. Online rooms, sign-up and SMS switch on once the server goes live.",
    serverOnly: "This works once the server goes live.",
    playBots: "Play with bots",
  },
  notFound: {
    title: "We couldn’t find this page",
    sub: "Looks like someone’s bluffing 😏",
    home: "Back home",
  },
};

export default en;
